import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _formatErrorMessage from "@mui/x-internals/formatErrorMessage";
const _excluded = ["count", "until"],
  _excluded2 = ["until"];
import { extractStandaloneEvent, dateToEventString, getDateKey, getOccurrenceEnd, mergeDateAndTime } from '@mui/x-scheduler-internals/internals';
import { getRemainingOccurrences, getWeekDayCode, NOT_LOCALIZED_WEEK_DAYS, parsesByDayForWeeklyFrequency } from "./internal-utils.mjs";
import { computeMonthlyOrdinal } from "./computeMonthlyOrdinal.mjs";
import { hasOccurrenceBefore, hasRemainingOccurrence } from "./seriesOccurrence.mjs";
export function updateRecurringEvent(adapter, originalEvent, occurrenceStart, changes, scope) {
  switch (scope) {
    case 'this-and-following':
      {
        return applyRecurringUpdateFollowing(adapter, originalEvent, occurrenceStart, changes);
      }
    case 'all':
      {
        return applyRecurringUpdateAll(adapter, originalEvent, occurrenceStart, changes);
      }
    case 'only-this':
      {
        return applyRecurringUpdateOnlyThis(adapter, originalEvent, occurrenceStart, changes);
      }
    default:
      {
        throw new Error(process.env.NODE_ENV !== "production" ? `MUI X Scheduler: The scope "${scope}" is not supported for recurring events. ` + 'Supported scopes are "all", "only-this", and "this-and-following". ' + 'Use one of the supported scope values.' : _formatErrorMessage(243, scope));
      }
  }
}

/**
 * Applies a "this and following" update to a recurring series by splitting it into:
 * - the original series truncated up to the day before the edited occurrence, and
 * - a new series starting at the edited occurrence with the requested changes.
 * @returns The updated list of events with the split applied.
 */
export function applyRecurringUpdateFollowing(adapter, originalEvent, occurrenceStart, changes) {
  const newStart = changes.start ?? occurrenceStart;

  // 1) Old series: truncate rule to end the day before the edited occurrence
  const occurrenceDayStart = adapter.startOfDay(occurrenceStart);
  const untilDate = adapter.addDays(occurrenceDayStart, -1);
  const originalRule = originalEvent.dataTimezone.rrule;
  const baseRule = _objectWithoutPropertiesLoose(originalRule, _excluded);

  // 2) New event: apply changes, decide RRULE for the new series
  const newRRule = decideSplitRRule(adapter, originalRule, originalEvent.dataTimezone.start.value, occurrenceStart, changes);
  const newEventId = `${originalEvent.id}::${getDateKey(newStart, adapter)}`;
  const originalModel = originalEvent.modelInBuiltInFormat;
  const dataTimezone = originalModel.timezone ?? 'default';
  const stringified = _extends({}, changes);
  // When start/end are not explicitly changed, the new series must start at the occurrence
  // date (not at DTSTART), otherwise the split series and the truncated original would overlap.
  stringified.start = dateToEventString(adapter, newStart, originalModel.start, dataTimezone);
  const occurrenceEnd = getOccurrenceEnd({
    adapter,
    event: originalEvent,
    occurrenceStart
  });
  const effectiveEnd = changes.end ?? occurrenceEnd;
  stringified.end = dateToEventString(adapter, effectiveEnd, originalModel.end, dataTimezone);
  if (changes.exDates != null) {
    stringified.exDates = changes.exDates.map((d, i) => {
      const originalExDate = originalModel.exDates?.[i];
      if (originalExDate) {
        return dateToEventString(adapter, d, originalExDate, dataTimezone);
      }
      return dateToEventString(adapter, d, originalModel.start, dataTimezone);
    });
  }
  let newEventRRule;
  if (newRRule != null) {
    if (newRRule.until != null) {
      newEventRRule = _extends({}, newRRule, {
        until: dateToEventString(adapter, newRRule.until, originalModel.start, dataTimezone)
      });
    } else {
      const rest = _objectWithoutPropertiesLoose(newRRule, _excluded2);
      newEventRRule = rest;
    }
  }
  const newEvent = _extends({}, originalEvent.modelInBuiltInFormat, stringified, {
    id: newEventId,
    rrule: newEventRRule,
    extractedFromId: originalEvent.modelInBuiltInFormat.id
  });

  // 3) Drop the original series when no occurrence remains before the edit, otherwise truncate it.
  if (!hasOccurrenceBefore(adapter, originalEvent, occurrenceStart)) {
    return {
      created: [newEvent],
      deleted: [originalEvent.id]
    };
  }
  return {
    created: [newEvent],
    updated: [{
      id: originalEvent.id,
      rrule: _extends({}, baseRule, {
        until: untilDate
      })
    }]
  };
}

/**
 * Applies an "all events" update to a recurring series.
 *
 * Rules:
 * - If the edited occurrence is not the first, keeps the original DTSTART
 *   and adjusts the RRULE pattern (e.g. weekday swap) so all past and future
 *   events follow the new pattern.
 * - If the edited occurrence is the first of the series, updates DTSTART/DTEND directly.
 * - When only the time changes, merges the new time into the original date.
 * @returns The updated list of events.
 */
export function applyRecurringUpdateAll(adapter, originalEvent, occurrenceStart, changes) {
  const eventUpdatedProperties = _extends({}, changes);

  // 1) Detect if caller changed the date part of start or end (vs only time)
  const occurrenceEnd = getOccurrenceEnd({
    adapter,
    occurrenceStart,
    event: originalEvent
  });
  const touchedStartDate = changes.start != null && !adapter.isSameDay(occurrenceStart, changes.start);
  const touchedEndDate = changes.end != null && !adapter.isSameDay(occurrenceEnd, changes.end);

  // 2) Is the edited occurrence the first of the series (DTSTART)?
  const editedIsDtstart = adapter.isSameDay(occurrenceStart, originalEvent.dataTimezone.start.value);

  // 3) Decide new start/end
  if (changes.start != null) {
    if (touchedStartDate) {
      // Date changed
      if (editedIsDtstart) {
        // First occurrence: allow moving DTSTART date
        eventUpdatedProperties.start = changes.start;
      } else {
        // Not first: keep original DTSTART date, merge only time
        eventUpdatedProperties.start = mergeDateAndTime(adapter, originalEvent.dataTimezone.start.value, changes.start);
      }
    } else {
      // Same day -> merge time into original date
      eventUpdatedProperties.start = mergeDateAndTime(adapter, originalEvent.dataTimezone.start.value, changes.start);
    }
  }
  if (changes.end != null) {
    if (touchedEndDate) {
      if (editedIsDtstart) {
        eventUpdatedProperties.end = changes.end;
      } else {
        eventUpdatedProperties.end = mergeDateAndTime(adapter, originalEvent.dataTimezone.end.value, changes.end);
      }
    } else {
      eventUpdatedProperties.end = mergeDateAndTime(adapter, originalEvent.dataTimezone.end.value, changes.end);
    }
  }

  // 4) RRULE adjustment: only if day changed, the event is recurring, and the user did not
  // provide an explicit rrule (same hasOwnProperty guard used in decideSplitRRule).
  const hasExplicitRRule = Object.prototype.hasOwnProperty.call(changes, 'rrule');
  if ((touchedStartDate || touchedEndDate) && originalEvent.dataTimezone.rrule && !hasExplicitRRule) {
    const newOccurrenceStart = changes.start ?? occurrenceStart;
    eventUpdatedProperties.rrule = adjustRRuleForAllMove(adapter, originalEvent.dataTimezone.rrule, occurrenceStart, newOccurrenceStart);
  }

  // 5) Return the updated event
  return {
    updated: [eventUpdatedProperties]
  };
}

/**
 * Applies a "only-this" update to a recurring series by:
 *  - creating a detached one-off event with the requested changes, and
 *  - adding an EXDATE to the original event to exclude the occurrence from the series.
 * @returns The updated list of events.
 */
export function applyRecurringUpdateOnlyThis(adapter, originalEvent, occurrenceStart, changes) {
  const originalModel = originalEvent.modelInBuiltInFormat;
  const dataTimezone = originalModel.timezone ?? 'default';
  const stringifiedChanges = _extends({}, changes);
  if (changes.start != null) {
    stringifiedChanges.start = dateToEventString(adapter, changes.start, originalModel.start, dataTimezone);
  }
  if (changes.end != null) {
    stringifiedChanges.end = dateToEventString(adapter, changes.end, originalModel.end, dataTimezone);
  }
  const exDates = [...(originalEvent.dataTimezone.exDates ?? []), adapter.startOfDay(occurrenceStart)];
  const created = [extractStandaloneEvent(originalEvent, stringifiedChanges)];
  if (!hasRemainingOccurrence(adapter, originalEvent, exDates)) {
    return {
      created,
      deleted: [originalEvent.id]
    };
  }
  return {
    created,
    updated: [{
      id: originalEvent.id,
      exDates
    }]
  };
}

/**
 * Adjusts a recurring event's RRULE when applying an "all" update that changes the weekday.
 *
 * Rules:
 * - WEEKLY: realign BYDAY by swapping the weekday of the edited occurrence
 *   with the weekday of the destination.
 * - MONTHLY:
 *   - If BYMONTHDAY is used → set it to the new start date's day of month.
 *   - If BYDAY (ordinal) is used → recompute the ordinal (e.g. 2TU → 3WE) based on the new start.
 * @returns The adjusted RRULE object, or the original rrule if no change is needed.
 */
export function adjustRRuleForAllMove(adapter, rrule, occurrenceStart, newStart) {
  const nextRRule = _extends({}, rrule);
  if (rrule.freq === 'WEEKLY') {
    const normalized = parsesByDayForWeeklyFrequency(rrule.byDay) ?? [getWeekDayCode(adapter, occurrenceStart)];
    nextRRule.byDay = realignWeeklyByDay(adapter, normalized, occurrenceStart, newStart);
  } else if (rrule.freq === 'MONTHLY') {
    // BYMONTHDAY → match the new calendar day
    if (rrule.byMonthDay?.length) {
      nextRRule.byMonthDay = [adapter.getDate(newStart)];
    }
    // Ordinal BYDAY → recompute ordinal + weekday for newStart
    if (rrule.byDay?.length) {
      const code = getWeekDayCode(adapter, newStart);
      const ord = computeMonthlyOrdinal(adapter, newStart);
      nextRRule.byDay = [`${ord}${code}`];
    }
  }
  return nextRRule;
}

/**
 * Decides the RRULE for the split (new) segment when editing "this and following".
 *
 * Rules:
 * - If user provided changes.rrule → use it as-is (preserve COUNT/UNTIL).
 * - If changes.rrule is explicitly undefined → non-recurring one-off.
 * - If changes.rrule is omitted → inherit pattern and recompute boundaries:
 *   * WEEKLY: if `changes.start` is provided, realign BYDAY by swapping the weekday of the
 *     edited occurrence with the weekday of the new start (dedupe if needed).
 *   * MONTHLY:
 *       - If BYMONTHDAY is present: set it to the day of `changes.start`.
 *       - If BYDAY (ordinal) is present: recompute `{ord}{code}` using `computeMonthlyOrdinal`
 *         and the weekday of `changes.start`.
 *   * Boundaries:
 *       - If original had COUNT: set COUNT = remaining occurrences from the split day.
 *       - If original had UNTIL: keep the same UNTIL.
 */
export function decideSplitRRule(adapter, originalRule, originalSeriesStart, splitStart, changes) {
  // Detect whether user touched rrule at all
  const hasRRuleProp = Object.prototype.hasOwnProperty.call(changes, 'rrule');
  const changesRRule = changes.rrule;

  // Case A — user provided a new RRULE → respect it (including COUNT/UNTIL)
  if (hasRRuleProp && changesRRule) {
    return changesRRule;
  }

  // Case B — user explicitly removed recurrence → one-off
  if (hasRRuleProp && !changesRRule) {
    return undefined;
  }

  // Case C — user did not touch RRULE → inherit pattern and recompute boundaries
  const realignedRule = _extends({}, originalRule);

  // Freq WEEKLY: realign BYDAY, swap the old weekday for the new one while preserving the rest of the weekly pattern.
  if (originalRule.freq === 'WEEKLY' && originalRule.byDay?.length && changes.start) {
    realignedRule.byDay = realignWeeklyByDay(adapter, originalRule.byDay, adapter.startOfDay(splitStart), changes.start);
  }
  // Freq MONTHLY realignment
  if (originalRule.freq === 'MONTHLY' && changes.start) {
    // A) BYMONTHDAY → set to the new calendar day
    if (originalRule.byMonthDay?.length) {
      realignedRule.byMonthDay = [adapter.getDate(changes.start)];
    }

    // B) Ordinal BYDAY → recompute ordinal + weekday for the new date
    if (originalRule.byDay?.length) {
      const code = getWeekDayCode(adapter, changes.start);
      const ord = computeMonthlyOrdinal(adapter, changes.start);
      realignedRule.byDay = [`${ord}${code}`];
    }
  }

  // Recalculate COUNT: original minus prior occurrences.
  if (originalRule.count) {
    const dayBefore = adapter.addDays(adapter.startOfDay(splitStart), -1);
    const remaining = getRemainingOccurrences(adapter, originalRule, originalSeriesStart, dayBefore, originalRule.count);
    // If no occurrences remain, the split segment is non-recurring.
    if (remaining <= 0) {
      return undefined;
    }
    realignedRule.count = remaining;
  } else if (originalRule.until) {
    realignedRule.until = originalRule.until;
  }
  return realignedRule;
}

/**
 * Realigns a WEEKLY BYDAY pattern when splitting “this and following”.
 * Swaps the weekday of the edited occurrence (oldRefDay) with the weekday of the new
 * series start (newStart), preserving the rest of the pattern and avoiding duplicates.
 * @returns {RecurringEventByDayValue[]} - The realigned BYDAY list (deduplicated).
 */
export function realignWeeklyByDay(adapter, weekDayCodes, oldRefDay, newStart) {
  const oldCode = getWeekDayCode(adapter, oldRefDay);
  const newCode = getWeekDayCode(adapter, newStart);
  if (oldCode === newCode) {
    return weekDayCodes;
  }
  const weekDayCodesSet = new Set(weekDayCodes);

  // Iterate in canonical RFC 5545 order (MO → SU), independent of locale.
  // NOT_LOCALIZED_WEEK_DAYS is already in canonical order, so no offset is needed.
  const newWeekDayCodes = [];
  for (let i = 0; i < NOT_LOCALIZED_WEEK_DAYS.length; i += 1) {
    const code = NOT_LOCALIZED_WEEK_DAYS[i];
    let shouldAddCode;
    // Only add the newCode if the oldCode was present
    if (code === newCode && weekDayCodesSet.has(oldCode)) {
      shouldAddCode = true;
    }
    // Only add other codes if they were present originally and are not the oldCode
    else if (weekDayCodesSet.has(code) && code !== oldCode) {
      shouldAddCode = true;
    } else {
      shouldAddCode = false;
    }
    if (shouldAddCode) {
      newWeekDayCodes.push(code);
    }
  }
  return newWeekDayCodes;
}